import React, { useEffect, useState } from 'react';
import { EChartsOption } from 'echarts';
import BaseChart from './BaseChart';
import { apiFetch } from '../../services/api/client';
import { DEFAULT_ARTIST_ID } from '../../config/nova-constants';
import {
  getCategoryAxisLayout,
  maxGridBottomPct,
  splitWordsOnSpace,
} from '../../utils/echartsCategoryAxis';
import { CHART_NO_DATA_FOR_ARTIST } from './chartMessages';

interface BrandAffinityData {
  brandName: string;
  affinityScore: number;
  rankPosition: number;
  category: string;
  color: string;
}

interface BrandAffinityResponse {
  artistId: string;
  data: BrandAffinityData[];
  totalBrands: number;
  source: string;
  timestamp: string;
}

interface BrandAffinityChartProps {
  artistId?: string;
  platformArtistId?: string;
  title?: string;
  className?: string;
  refreshKey?: number;
  platformMappingLoading?: boolean;
  platformMappingError?: string | null;
}

const BrandAffinityChart: React.FC<BrandAffinityChartProps> = ({
  artistId,
  platformArtistId,
  title = 'Brand Affinity',
  className = '',
  refreshKey = 0,
  platformMappingLoading = false,
  platformMappingError = null
}) => {
  const [data, setData] = useState<BrandAffinityData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalBrands, setTotalBrands] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // 1. Check if platform mapping is still loading
        if (platformMappingLoading) {
          return; // Don't start loading yet
        }
        
        // 2. Check if platform mapping failed
        if (platformMappingError) {
          throw new Error(`Platform mapping failed: ${platformMappingError}`);
        }
        
        // 3. Check if we have platformArtistId after mapping completed
        if (!platformArtistId) {
          throw new Error('No platform mapping found for this artist');
        }
        
        const response: BrandAffinityResponse = await apiFetch(
          `/multi-analytics/brand-affinity/${artistId || DEFAULT_ARTIST_ID}`
        );

        const rows = Array.isArray(response?.data) ? response.data : [];
        setData(rows);
        setTotalBrands(typeof response?.totalBrands === 'number' ? response.totalBrands : rows.length);
        
      } catch (err: any) {
        setError(err.message || 'Failed to load brand affinity data');
        
        // Only show fallback data if it's a real API error, not a mapping issue
        if (!platformMappingError && platformArtistId) {
          setData([
            { brandName: 'Gucci', affinityScore: 1.7, rankPosition: 1, category: 'Low', color: '#EF4444' },
            { brandName: 'Vans', affinityScore: 1.5, rankPosition: 2, category: 'Low', color: '#EF4444' },
            { brandName: 'Nike', affinityScore: 1.4, rankPosition: 3, category: 'Low', color: '#EF4444' },
            { brandName: 'Mercedes-Benz', affinityScore: 1.4, rankPosition: 4, category: 'Low', color: '#EF4444' },
            { brandName: 'Fashion Nova', affinityScore: 1.4, rankPosition: 5, category: 'Low', color: '#EF4444' }
          ]);
          setTotalBrands(5);
        }
      } finally {
        // Only set loading to false if we're not waiting for platform mapping
        if (!platformMappingLoading) {
          setLoading(false);
        }
      }
    };

    fetchData();
  }, [artistId, platformArtistId, refreshKey, platformMappingLoading, platformMappingError]);

  const getChartOption = (): EChartsOption => {
    if (!data || data.length === 0) return {};

    // Sort by affinity score (descending)
    const sortedData = [...data].sort((a, b) => b.affinityScore - a.affinityScore);
    const xLayout = getCategoryAxisLayout(sortedData.length, 'longLabels');

    return {
      backgroundColor: '#ffffff',
      tooltip: {
        trigger: 'axis',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#e5e7eb',
        borderWidth: 1,
        borderRadius: 8,
        textStyle: {
          color: '#1f2937',
          fontSize: 12
        },
        confine: true, // Keep tooltip within chart bounds
        position: function (point: any, params: any, dom: any, rect: any, size: any) {
          // Smart positioning to avoid clipping at edges
          const tooltipWidth = size.contentSize[0];
          const tooltipHeight = size.contentSize[1];
          const chartWidth = size.viewSize[0];
          const chartHeight = size.viewSize[1];
          
          // Default position with offset
          let x = point[0] + 10;
          let y = point[1] - tooltipHeight - 10;
          
          // Adjust if tooltip would overflow right edge
          if (x + tooltipWidth > chartWidth - 10) {
            x = point[0] - tooltipWidth - 10;
          }
          
          // Adjust if tooltip would overflow top edge
          if (y < 10) {
            y = point[1] + 20;
          }
          
          // Ensure minimum distances from edges
          x = Math.max(10, Math.min(x, chartWidth - tooltipWidth - 10));
          y = Math.max(10, Math.min(y, chartHeight - tooltipHeight - 10));
          
          return [x, y];
        },
        formatter: function(params: any) {
          const dataIndex = params[0].dataIndex;
          const item = sortedData[dataIndex];
          
          return `
            <div style="font-weight: 600; margin-bottom: 8px; color: #fff;">${item.brandName}</div>
            <div style="color: ${item.color}; margin-bottom: 4px;">
              ● Affinity Score: ${item.affinityScore.toFixed(1)}
            </div>
            <div style="color: #9ca3af; font-size: 11px; margin-bottom: 4px;">
              Category: ${item.category}
            </div>
            <div style="color: #9ca3af; font-size: 11px;">
              Rank: #${item.rankPosition}
            </div>
          `;
        }
      },
      grid: {
        left: '12%',
        right: '8%',
        top: '10%',
        bottom: maxGridBottomPct('20%', xLayout.gridBottom),
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: sortedData.map(item => item.brandName),
        axisLine: {
          lineStyle: {
            color: '#d1d5db'
          }
        },
        axisLabel: {
          color: '#6b7280',
          fontSize: xLayout.fontSize,
          rotate: xLayout.rotate,
          interval: 0,
          lineHeight: 13,
          formatter: (value: string) =>
            value.includes('-')
              ? value.split('-').join('\n')
              : splitWordsOnSpace(value, 11)
        },
        axisTick: {
          show: false
        }
      },
      yAxis: {
        type: 'value',
        min: 0,
        max: 5,
        axisLine: {
          show: false
        },
        axisLabel: {
          color: '#6b7280',
          fontSize: 10,
          formatter: function(value: number) {
            return value.toFixed(1);
          }
        },
        splitLine: {
          lineStyle: {
            color: '#d1d5db',
            width: 1
          }
        },
        splitNumber: 5
      },
      series: [
        {
          name: 'Brand Affinity',
          type: 'bar',
          data: sortedData.map((item) => ({
            value: item.affinityScore,
            itemStyle: {
              color: item.color,
              borderRadius: [4, 4, 0, 0]
            }
          })),
          barWidth: '60%',
          emphasis: {
            itemStyle: {
              shadowBlur: 8,
              shadowColor: 'rgba(0, 0, 0, 0.3)'
            }
          },
          label: {
            show: true,
            position: 'top',
            color: '#6b7280',
            fontSize: 10,
            formatter: function(params: any) {
              return params.value.toFixed(1);
            }
          }
        }
      ]
    };
  };

  if (loading) {
    // Different loading messages based on state
    let loadingMessage = 'Loading...';
    let loadingSubtext = 'FETCHING BRAND DATA';
    
    if (platformMappingLoading) {
      loadingMessage = 'Initializing...';
      loadingSubtext = 'LOADING ARTIST MAPPING';
    } else if (platformArtistId) {
      loadingMessage = 'Loading...';
      loadingSubtext = 'FETCHING BRAND DATA';
    }
    
    return (
      <div className="w-full h-full bg-transparent p-4 flex flex-col">
        <div className="flex items-center mb-6">
          <div>
            <div className="text-2xl font-bold text-gray-400">{loadingMessage}</div>
            <div className="text-sm text-gray-400">{loadingSubtext}</div>
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400"></div>
        </div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="w-full h-full bg-transparent p-4 flex flex-col">
        <div className="flex flex-1 flex-col items-center justify-center px-3 text-center">
          <p className="text-red-400 text-sm">{CHART_NO_DATA_FOR_ARTIST}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full bg-transparent flex flex-col" style={{ paddingLeft: '1rem', paddingRight: '1rem', paddingBottom: '1rem' }}>
      <div className="w-full min-h-0 h-full">
        <BaseChart
          option={getChartOption()}
          loading={loading}
          className="w-full h-full"
        />
      </div>
      {/* Table view commented per request; keep for future use
      <div className="w-full" style={{ height: '40%', paddingBottom: '45px' }}>
        <div className="h-full border border-gray-700 rounded-lg overflow-hidden">
          <div className="h-full overflow-x-auto overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#1C1C1C] border-b border-gray-600 sticky top-0 z-10">
                <tr>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">RANK</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">BRAND</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">SCORE</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">CATEGORY</th>
                </tr>
              </thead>
              <tbody>
                {data
                  .sort((a, b) => a.rankPosition - b.rankPosition)
                  .map((item, index) => (
                    <tr key={`${item.brandName}-${index}`} className={`${index !== data.length - 1 ? 'border-b border-gray-700/50' : ''}`}>
                      <td className="py-3 px-4 text-left">
                        <span className="text-gray-900 font-bold">#{item.rankPosition}</span>
                      </td>
                      <td className="py-3 px-4 text-left">
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-4 h-4 rounded-full flex-shrink-0"
                            style={{ backgroundColor: item.color }}
                          ></div>
                          <span className="text-gray-900 font-medium truncate">{item.brandName}</span>
                        </div>
                      </td>
                      <td className="text-left py-3 px-4">
                        <span className="text-gray-900 font-bold">
                          {item.affinityScore.toFixed(1)}
                        </span>
                      </td>
                      <td className="text-left py-3 px-4">
                        <span 
                          className="px-2 py-1 rounded text-xs font-medium"
                          style={{ 
                            backgroundColor: `${item.color}20`, 
                            color: item.color 
                          }}
                        >
                          {item.category}
                        </span>
                      </td>
                    </tr>
                  ))}
                <tr className="h-4">
                  <td colSpan={4}></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
      */}
      {error && data.length > 0 && (
        <div className="mt-2">
          <p className="text-xs text-yellow-400">Sample data is shown.</p>
        </div>
      )}
    </div>
  );
};

export default BrandAffinityChart;
